import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Card,
  CardContent,
  Stack,
  Divider,
  styled,
  Fade
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import moment from 'moment';
import {
  Person,
  EventNote,
  AccessTime,
  WhatsApp,
  Send,
  ArrowBack,
  ArrowForward
} from '@mui/icons-material';

const steps = ['Personal Information', 'Service Selection', 'Schedule Appointment', 'Confirmation'];

const services = [
  {
    title: 'Orthopedic Physiotherapy',
    description: 'Expert treatment for musculoskeletal conditions, joint pain, and post-injury rehabilitation.',
    image: 'ortho.png'
  },
  {
    title: 'Manual Therapy',
    description: 'Specialized hands-on treatment techniques to reduce pain, improve joint mobility, and restore muscle function.',
    image: 'Manual Therapy.png'
  },
  {
    title: 'Cardiac Rehabilitation',
    description: 'Comprehensive rehabilitation program for heart patients, focusing on safe exercise progression and heart health.',
    image: 'Cardiac.png'
  },
  {
    title: 'Women\'s Health Physiotherapy',
    description: 'Specialized physiotherapy services designed specifically for women\'s unique health needs.',
    image: 'womenhealth.png'
  },
  {
    title: 'Sports Rehabilitation',
    description: 'Comprehensive rehabilitation programs for athletes and sports enthusiasts.',
    image: 'sports.png'
  },
  {
    title: 'Vestibular Rehabilitation',
    description: 'Specialized treatment for dizziness, vertigo, and balance disorders.',
    image: 'Vestibular rehabilitation.png'
  },
  {
    title: 'Home Care Physiotherapy',
    description: 'Professional physiotherapy care in the comfort of your home.',
    image: 'homecare.png'
  },
  {
    title: 'Geriatric Physiotherapy',
    description: 'Specialized care for elderly patients focusing on improving mobility and balance.',
    image: 'geriatric.png'
  },
  {
    title: 'Post-Surgical Rehabilitation',
    description: 'Specialized rehabilitation programs for optimal recovery after surgical procedures.',
    image: 'postsurgical.png'
  },
  {
    title: 'Neurological Rehabilitation',
    description: 'Expert care for patients with neurological conditions.',
    image: 'neuro.png'
  },
  {
    title: 'Electrotherapy',
    description: 'Advanced electrical stimulation treatments to reduce pain and improve muscle function.',
    image: 'electrotherapy.png'
  }
];

const timeSlots = {
  morning: [
    { label: '9:00 AM', value: '09:00' },
    { label: '9:45 AM', value: '09:45' },
    { label: '10:30 AM', value: '10:30' },
    { label: '11:15 AM', value: '11:15' },
    { label: '12:00 PM', value: '12:00' },
    { label: '12:45 PM', value: '12:45' }
  ],
  evening: [
    { label: '4:00 PM', value: '16:00' },
    { label: '4:45 PM', value: '16:45' },
    { label: '5:30 PM', value: '17:30' },
    { label: '6:15 PM', value: '18:15' },
    { label: '7:00 PM', value: '19:00' },
    { label: '7:45 PM', value: '19:45' }
  ]
};

// Simplified AnimatedContainer without problematic transitions
const AnimatedContainer = styled(Box)(({ theme }) => ({
  '& .MuiTextField-root, & .MuiFormControl-root, & .MuiButton-root': {
    opacity: 1,
    transform: 'translateY(0)',
    transition: 'opacity 0.3s ease, transform 0.3s ease',
  }
}));

const AnimatedStep = styled(Step)(({ theme, active }) => ({
  '& .MuiStepLabel-root': {
    transition: 'transform 0.3s ease',
    transform: active ? 'scale(1.1)' : 'scale(1)',
  },
  '& .MuiStepIcon-root': {
    transition: 'transform 0.3s ease, filter 0.3s ease',
    transform: active ? 'scale(1.2)' : 'scale(1)',
    filter: active ? 'drop-shadow(0 4px 8px rgba(0, 150, 136, 0.3))' : 'none',
  },
  '& .MuiStepLabel-label': {
    transition: 'all 0.3s ease',
    fontWeight: active ? 600 : 400,
    color: active ? theme.palette.primary.main : theme.palette.text.secondary,
  },
}));

const NavigationButtons = ({ activeStep, handleBack, handleNext, isLastStep }) => {
  const scrollToTop = () => {
    window.scrollTo({
      top: document.querySelector('.MuiCard-root').offsetTop - 100,
      behavior: 'smooth'
    });
  };

  const handleNextWithAnimation = () => {
    const button = document.querySelector('.next-button');
    button.style.transform = 'translateX(100px)';
    button.style.opacity = '0';
    
    setTimeout(() => {
      handleNext();
      setTimeout(() => {
        button.style.transform = 'translateX(-100px)';
        requestAnimationFrame(() => {
          button.style.transform = 'translateX(0)';
          button.style.opacity = '1';
        });
      }, 50);
    }, 300);

    scrollToTop();
  };

  const handleBackWithAnimation = () => {
    const button = document.querySelector('.back-button');
    button.style.transform = 'translateX(-100px)';
    button.style.opacity = '0';
    
    setTimeout(() => {
      handleBack();
      setTimeout(() => {
        button.style.transform = 'translateX(100px)';
        requestAnimationFrame(() => {
          button.style.transform = 'translateX(0)';
          button.style.opacity = '1';
        });
      }, 50);
    }, 300);

    scrollToTop();
  };

  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
      <Fade in={activeStep > 0} timeout={500}>
        <Box sx={{ position: 'relative' }}>
          <Button
            className="back-button"
            variant="outlined"
            onClick={handleBackWithAnimation}
            startIcon={<ArrowBack />}
            sx={{
              opacity: activeStep > 0 ? 1 : 0,
              transform: 'translateX(0)',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              color: '#009688',
              borderColor: '#009688',
              '&:hover': {
                borderColor: '#00796b',
                backgroundColor: 'rgba(0, 150, 136, 0.08)',
                transform: 'translateX(-5px)'
              }
            }}
          >
            Back
          </Button>
        </Box>
      </Fade>

      <Fade in timeout={500}>
        <Box sx={{ position: 'relative' }}>
          <Button
            className="next-button"
            variant="contained"
            onClick={handleNextWithAnimation}
            endIcon={<ArrowForward />}
            sx={{
              transform: 'translateX(0)',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              background: 'linear-gradient(45deg, #009688, #4CAF50)',
              '&:hover': {
                background: 'linear-gradient(45deg, #00897B, #43A047)',
                transform: 'translateX(5px)'
              }
            }}
          >
            {isLastStep ? 'Book Appointment' : 'Next'}
          </Button>
        </Box>
      </Fade>
    </Box>
  );
};

function BookAppointment() {
  const [activeStep, setActiveStep] = useState(0);
  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    service: '',
    date: null,
    time: null,
    notes: ''
  });
  const [whatsappNumber, setWhatsappNumber] = useState(Number(localStorage.getItem('whatsappNumber')) || 7619678243);
  const [clinicAddress, setClinicAddress] = useState(localStorage.getItem('clinicAddress') || '');

  useEffect(() => {
    const handleStorageChange = () => {
      setWhatsappNumber(Number(localStorage.getItem('whatsappNumber')) || 7619678243);
      setClinicAddress(localStorage.getItem('clinicAddress') || '');
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  const validateStep = (step) => {
    const newErrors = {};
    
    switch (step) {
      case 0:
        if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
        if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';
        if (!formData.email.trim()) {
          newErrors.email = 'Email is required';
        } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
          newErrors.email = 'Please enter a valid email';
        }
        if (!formData.phone.trim()) {
          newErrors.phone = 'Phone number is required';
        } else if (!/^\+?[\d\s-]{10,}$/.test(formData.phone.replace(/\s+/g, ''))) {
          newErrors.phone = 'Please enter a valid phone number';
        }
        break;
      
      case 1:
        if (!formData.service) newErrors.service = 'Please select a service';
        break;
      
      case 2:
        if (!formData.date) newErrors.date = 'Please select a date';
        if (!formData.time) newErrors.time = 'Please select a time';
        break;
      
      default:
        break;
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(activeStep)) {
      setActiveStep((prevStep) => prevStep + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
    setErrors({});
  };

  const handleInputChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
    if (errors[field]) {
      setErrors({ ...errors, [field]: null });
    }
    // Automatically move to next step when service is selected
    if (field === 'service') {
      setActiveStep((prevStep) => prevStep + 1);
    }
  };

  const handleDateChange = (newDate) => {
    try {
      const momentDate = moment.isMoment(newDate) ? newDate : moment(newDate);
      if (momentDate.isValid()) {
        setFormData({ ...formData, date: momentDate });
        if (errors.date) {
          setErrors({ ...errors, date: null });
        }
      }
    } catch (error) {
      console.error('Date parsing error:', error);
    }
  };

  const handleTimeChange = (slot) => {
    setFormData({
      ...formData,
      time: slot.value
    });
    setErrors({
      ...errors,
      time: ''
    });
  };

  const handleWhatsAppBooking = () => {
    if (validateStep(activeStep)) {
      const formattedDate = moment(formData.date).format('dddd, MMMM D, YYYY');
      const selectedTimeSlot = [...timeSlots.morning, ...timeSlots.evening].find(slot => slot.value === formData.time);
      const formattedTime = selectedTimeSlot ? selectedTimeSlot.label : formData.time;
      
      const message = `🌟 𝐂𝐀𝐑𝐄𝐅𝐋𝐄𝐗 𝐏𝐇𝐘𝐒𝐈𝐎𝐓𝐇𝐄𝐑𝐀𝐏𝐘 🌟
━━━━━━━━━━━━━━━━━━━━━━━━

👤 𝐏𝐚𝐭𝐢𝐞𝐧𝐭 𝐈𝐧𝐟𝐨𝐫𝐦𝐚𝐭𝐢𝐨𝐧
┌───────────────────
│ 🎯 Name: ${formData.firstName} ${formData.lastName}
│ 📱 Phone: ${formData.phone}
│ 📧 Email: ${formData.email}
└───────────────────

🏥 𝐀𝐩𝐩𝐨𝐢𝐧𝐭𝐦𝐞𝐧𝐭 𝐃𝐞𝐭𝐚𝐢𝐥𝐬
┌───────────────────
│ 🔍 Service: ${formData.service}
│ 📅 Date: ${formattedDate}
│ ⏰ Time: ${formattedTime}
└───────────────────

${formData.notes ? `📝 𝐒𝐩𝐞𝐜𝐢𝐚𝐥 𝐍𝐨𝐭𝐞𝐬
┌───────────────────
│ ${formData.notes.split('\n').join('\n│ ')}
└───────────────────\n\n` : ''}

ℹ️ 𝐈𝐦𝐩𝐨𝐫𝐭𝐚𝐧𝐭 𝐑𝐞𝐦𝐢𝐧𝐝𝐞𝐫𝐬
┌───────────────────
│ ⏱️ Please arrive 10 minutes early
│ 📋 Bring relevant medical reports
│ ⌛ Session duration: 45 minutes
└───────────────────

📍 𝐕𝐢𝐬𝐢𝐭 𝐔𝐬 𝐀𝐭
┌───────────────────
│ CAREFLEX PHYSIOTHERAPY
│ ${clinicAddress.split('\n').join('\n│ ')}
└───────────────────

💬 𝐍𝐞𝐞𝐝 𝐀𝐬𝐬𝐢𝐬𝐭𝐚𝐧𝐜𝐞?
┌───────────────────
│ 📞 Call us: +${whatsappNumber}
│ 🌐 Visit: careflexphysiotherapy.com
└───────────────────

✨ Thank you for choosing CareFlex! 
We look forward to helping you on your wellness journey. 🙏

#CareFlex #Physiotherapy #WellnessCare`;

      const encodedMessage = encodeURIComponent(message.trim());
      window.open(`https://wa.me/${whatsappNumber}?text=${encodedMessage}`, '_blank');
      
      // Reset form after WhatsApp redirection
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        service: '',
        date: null,
        time: null,
        notes: ''
      });
      setActiveStep(0);
    }
  };

  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Personal Information
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  fullWidth
                  label="First Name"
                  value={formData.firstName}
                  onChange={(e) => handleInputChange('firstName', e.target.value)}
                  error={!!errors.firstName}
                  helperText={errors.firstName}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  fullWidth
                  label="Last Name"
                  value={formData.lastName}
                  onChange={(e) => handleInputChange('lastName', e.target.value)}
                  error={!!errors.lastName}
                  helperText={errors.lastName}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  fullWidth
                  label="Email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  error={!!errors.email}
                  helperText={errors.email}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  fullWidth
                  label="Phone"
                  value={formData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  error={!!errors.phone}
                  helperText={errors.phone}
                />
              </Grid>
            </Grid>
          </Box>
        );
      case 1:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Select Service
            </Typography>
            <Grid container spacing={3}>
              {services.map((service, index) => (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <Card
                    onClick={() => handleInputChange('service', service.title)}
                    sx={{
                      cursor: 'pointer',
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      transition: 'all 0.3s ease',
                      border: formData.service === service.title ? '2px solid' : '1px solid',
                      borderColor: formData.service === service.title ? 'primary.main' : 'divider',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: 3,
                      },
                    }}
                  >
                    <Box sx={{ position: 'relative', paddingTop: '60%' }}>
                      <Box
                        component="img"
                        src={service.image}
                        alt={service.title}
                        sx={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover'
                        }}
                      />
                    </Box>
                    <CardContent sx={{ flexGrow: 1 }}>
                      <Typography variant="h6" gutterBottom component="div">
                        {service.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {service.description}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        );
      case 2:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Schedule Appointment
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <LocalizationProvider dateAdapter={AdapterMoment}>
                  <DatePicker
                    label="Select Date"
                    value={formData.date}
                    onChange={(newValue) => handleInputChange('date', newValue)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        fullWidth
                        required
                        error={!!errors.date}
                        helperText={errors.date}
                      />
                    )}
                    minDate={moment()}
                  />
                </LocalizationProvider>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required error={!!errors.time}>
                  <InputLabel>Select Time</InputLabel>
                  <Select
                    value={formData.time || ''}
                    onChange={(e) => handleInputChange('time', e.target.value)}
                    label="Select Time"
                  >
                    <MenuItem disabled value="">
                      <em>Select a time slot</em>
                    </MenuItem>
                    <Typography variant="subtitle2" sx={{ px: 2, py: 1, color: 'text.secondary' }}>
                      Morning
                    </Typography>
                    {timeSlots.morning.map((slot) => (
                      <MenuItem key={slot.value} value={slot.value}>
                        {slot.label}
                      </MenuItem>
                    ))}
                    <Typography variant="subtitle2" sx={{ px: 2, py: 1, color: 'text.secondary' }}>
                      Evening
                    </Typography>
                    {timeSlots.evening.map((slot) => (
                      <MenuItem key={slot.value} value={slot.value}>
                        {slot.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Additional Notes"
                  multiline
                  rows={4}
                  value={formData.notes}
                  onChange={(e) => handleInputChange('notes', e.target.value)}
                  placeholder="Please provide any additional information or special requirements"
                />
              </Grid>
            </Grid>
          </Box>
        );
      case 3:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Confirm Your Appointment
            </Typography>
            <Stack spacing={2}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Personal Information
                </Typography>
                <Typography variant="body1">
                  {formData.firstName} {formData.lastName}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {formData.email} | {formData.phone}
                </Typography>
              </Box>
              <Divider />
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Service
                </Typography>
                <Typography variant="body1">{formData.service}</Typography>
              </Box>
              <Divider />
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Appointment Time
                </Typography>
                <Typography variant="body1">
                  {moment(formData.date).format('dddd, MMMM D, YYYY')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {formData.time}
                </Typography>
              </Box>
              {formData.notes && (
                <>
                  <Divider />
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Additional Notes
                    </Typography>
                    <Typography variant="body2">{formData.notes}</Typography>
                  </Box>
                </>
              )}
            </Stack>
          </Box>
        );
      default:
        return 'Unknown step';
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Hero Section with Doctor Image Background */}
      <Box 
        sx={{ 
          position: 'relative',
          height: '60vh',
          width: '100vw',
          left: '50%',
          right: '50%',
          marginLeft: '-50vw',
          marginRight: '-50vw',
          marginBottom: 8,
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: 'url(/cta-bg.jpg)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            filter: 'brightness(0.3)',
            transform: 'scale(1.1)',
            transition: 'transform 10s ease',
            animation: 'zoomEffect 20s infinite alternate',
            zIndex: 1
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(to bottom, rgba(0,0,0,0.4) 0%, rgba(0,0,0,0.6) 100%)',
            zIndex: 2
          },
          '@keyframes zoomEffect': {
            '0%': {
              transform: 'scale(1.1)'
            },
            '100%': {
              transform: 'scale(1.3)'
            }
          }
        }}
      >
        <Container 
          maxWidth="lg" 
          sx={{ 
            height: '100%', 
            display: 'flex', 
            flexDirection: 'column', 
            justifyContent: 'center',
            position: 'relative',
            zIndex: 3
          }}
        >
          <Fade in={true} timeout={1000}>
            <Box sx={{ textAlign: 'center' }}>
              <Box
                component="img"
                src="/new logo.png"
                alt="CareFlex Physiotherapy"
                sx={{
                  width: { xs: '150px', sm: '180px', md: '200px' },
                  height: 'auto',
                  mb: 4,
                  filter: 'brightness(0) invert(1)',
                  animation: 'floatAnimation 3s ease-in-out infinite',
                  '@keyframes floatAnimation': {
                    '0%, 100%': {
                      transform: 'translateY(0)'
                    },
                    '50%': {
                      transform: 'translateY(-10px)'
                    }
                  }
                }}
              />
              <Typography 
                variant="h2" 
                component="h1" 
                sx={{ 
                  color: 'white',
                  fontWeight: 700,
                  textShadow: '2px 2px 4px rgba(0,0,0,0.3)',
                  mb: 2,
                  fontSize: { xs: '2.5rem', sm: '3.5rem', md: '4rem' },
                  textAlign: 'center',
                  background: 'linear-gradient(45deg, #fff 30%, rgba(255,255,255,0.8) 90%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                Book Appointment
              </Typography>
              <Typography 
                variant="h5" 
                sx={{ 
                  color: 'rgba(255,255,255,0.9)',
                  maxWidth: '800px',
                  mx: 'auto',
                  textAlign: 'center',
                  textShadow: '1px 1px 2px rgba(0,0,0,0.2)',
                  fontSize: { xs: '1.1rem', sm: '1.3rem', md: '1.5rem' },
                  lineHeight: 1.6
                }}
              >
                Schedule your consultation with our expert physiotherapists today
              </Typography>
            </Box>
          </Fade>
        </Container>
      </Box>

      <Container maxWidth="md" sx={{ mt: -4, mb: 8 }}>
        <Card sx={{ borderRadius: '1rem', boxShadow: '0 8px 25px rgba(0, 150, 136, 0.15)' }}>
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ width: '100%', mb: 8 }}>
              <Stepper activeStep={activeStep} alternativeLabel>
                {steps.map((label, index) => (
                  <AnimatedStep key={label} active={activeStep === index}>
                    <StepLabel>{label}</StepLabel>
                  </AnimatedStep>
                ))}
              </Stepper>
            </Box>

            <AnimatedContainer>
              {getStepContent(activeStep)}
              
              <NavigationButtons 
                activeStep={activeStep}
                handleBack={handleBack}
                handleNext={activeStep === steps.length - 1 ? handleWhatsAppBooking : handleNext}
                isLastStep={activeStep === steps.length - 1}
              />
            </AnimatedContainer>
          </CardContent>
        </Card>
      </Container>
    </Container>
  );
}

const styles = `
  @keyframes pulse {
    0% {
      transform: scale(1);
      box-shadow: 0 0 0 0 rgba(0, 150, 136, 0.4);
    }
    70% {
      transform: scale(1.05);
      box-shadow: 0 0 0 10px rgba(0, 150, 136, 0);
    }
    100% {
      transform: scale(1);
      box-shadow: 0 0 0 0 rgba(0, 150, 136, 0);
    }
  }
`;

const styleSheet = document.createElement("style");
styleSheet.type = "text/css";
styleSheet.innerText = styles;
document.head.appendChild(styleSheet);

export default BookAppointment; 